import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collectionRequestAPI } from '../services/api';
import { LoadingSpinner } from '../components/LoadingSpinner';
import axios from 'axios';
import './Receipt.css';

interface ReceiptData {
  receipt_number: string;
  request_id: number;
  issued_date: string;
  collection_info: {
    address: string;
    waste_type: string;
    completed_at: string;
    collector_id: number;
  };
  volume_info: {
    estimated_volume?: number;
    actual_volume?: number;
    final_volume?: number;
  };
  user_info: {
    name: string;
    email: string;
  };
  collector_info?: {
    employee_id: string;
    name: string;
  };
  notes: {
    completion_notes?: string;
    review_notes?: string;
  };
  photos?: string[];
  pricing: {
    volume_charge: number;
    base_charge: number;
    total_amount: number;
    currency: string;
    rate_per_liter?: number;
    billing_volume?: number;
  };
}

export function Receipt() {
  const { id } = useParams<{ id: string }>();
  const [receipt, setReceipt] = useState<ReceiptData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [photoUrls, setPhotoUrls] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const fetchReceipt = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const response = await collectionRequestAPI.getReceipt(parseInt(id));
        console.log('Receipt data:', response);
        console.log('Volume info:', response.volume_info);
        console.log('Pricing info:', response.pricing);
        setReceipt(response);
        
        // 사진 URL을 React 앱의 정적 파일로 변경
        if (response.photos && response.photos.length > 0) {
          const photoUrlMap: { [key: string]: string } = {};
          for (const photo of response.photos) {
            // React 앱의 public 폴더에서 정적 파일 제공
            photoUrlMap[photo] = `/uploads${photo}`;
          }
          setPhotoUrls(photoUrlMap);
        }
      } catch (err: any) {
        console.error('Failed to fetch receipt:', err);
        setError(err.response?.data?.detail || '영수증을 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchReceipt();
  }, [id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDateOnly = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTimeOnly = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="receipt-page">
        <div className="loading-container">
          <LoadingSpinner />
          <p>영수증을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error || !receipt) {
    return (
      <div className="receipt-page">
        <div className="error-container">
          <h2>영수증을 불러올 수 없습니다</h2>
          <p>{error}</p>
          <Link to="/history" className="back-link">수거 내역으로 돌아가기</Link>
        </div>
      </div>
    );
  }

  const displayVolume = receipt.volume_info.final_volume || 
                       receipt.volume_info.actual_volume || 
                       receipt.volume_info.estimated_volume;
                       
  const billingVolume = receipt.pricing.billing_volume || displayVolume;

  return (
    <div className="receipt-page">
      <div className="receipt-container">
        <div className="receipt-header">
          <div className="receipt-logo">
            <h1>SSLMO</h1>
            <p>음식물 폐기물 수거 서비스</p>
          </div>
          <div className="receipt-title">
            <h2>수거 영수증</h2>
            <p className="receipt-number">{receipt.receipt_number}</p>
          </div>
        </div>

        <div className="receipt-info">
          <div className="info-section">
            <h3>고객 정보</h3>
            <div className="info-row">
              <span className="label">이름:</span>
              <span className="value">{receipt.user_info.name}</span>
            </div>
            <div className="info-row">
              <span className="label">이메일:</span>
              <span className="value">{receipt.user_info.email}</span>
            </div>
          </div>

          <div className="info-section">
            <h3>수거 정보</h3>
            <div className="info-row">
              <span className="label">수거 주소:</span>
              <span className="value">{receipt.collection_info.address}</span>
            </div>
            <div className="info-row">
              <span className="label">폐기물 종류:</span>
              <span className="value">음식물 폐기물</span>
            </div>
            <div className="info-row">
              <span className="label">수거 완료일:</span>
              <span className="value">{formatDateOnly(receipt.collection_info.completed_at)}</span>
            </div>
            <div className="info-row">
              <span className="label">수거 완료 시간:</span>
              <span className="value">{formatTimeOnly(receipt.collection_info.completed_at)}</span>
            </div>
            {receipt.collector_info && (
              <div className="info-row">
                <span className="label">수거 담당자:</span>
                <span className="value">{receipt.collector_info.name} ({receipt.collector_info.employee_id})</span>
              </div>
            )}
          </div>

          <div className="info-section">
            <h3>용량 정보</h3>
            {receipt.volume_info.estimated_volume && (
              <div className="info-row">
                <span className="label">예상 용량:</span>
                <span className="value">{receipt.volume_info.estimated_volume}L</span>
              </div>
            )}
            {receipt.volume_info.actual_volume && (
              <div className="info-row">
                <span className="label">실제 측정 용량:</span>
                <span className="value">{receipt.volume_info.actual_volume}L</span>
              </div>
            )}
            {receipt.volume_info.final_volume && (
              <div className="info-row">
                <span className="label">최종 확정 용량:</span>
                <span className="value important">{receipt.volume_info.final_volume}L</span>
              </div>
            )}
          </div>

          {(receipt.notes.completion_notes || receipt.notes.review_notes) && (
            <div className="info-section">
              <h3>메모</h3>
              {receipt.notes.completion_notes && (
                <div className="info-row">
                  <span className="label">작업 메모:</span>
                  <span className="value">{receipt.notes.completion_notes}</span>
                </div>
              )}
              {receipt.notes.review_notes && (
                <div className="info-row">
                  <span className="label">검토 메모:</span>
                  <span className="value">{receipt.notes.review_notes}</span>
                </div>
              )}
            </div>
          )}

          <div className="info-section pricing-section">
            <h3>요금 정보</h3>
            <div className="pricing-row">
              <span className="label">단가:</span>
              <span className="value">₩{receipt.pricing.rate_per_liter?.toLocaleString() || '200'}/L</span>
            </div>
            <div className="pricing-row">
              <span className="label">과금 기준 용량:</span>
              <span className="value">{billingVolume}L</span>
            </div>
            <div className="pricing-row total">
              <span className="label">총 금액:</span>
              <span className="value">₩{receipt.pricing.total_amount.toLocaleString()}</span>
            </div>
          </div>

          {receipt.photos && receipt.photos.length > 0 && (
            <div className="info-section">
              <h3>수거 증빙 사진</h3>
              <div className="photos-grid">
                {receipt.photos.map((photo, index) => (
                  <div key={index} className="receipt-photo">
                    <img
                      src={photo.startsWith('/uploads') ? photo : `/uploads${photo}`}
                      alt={`수거 사진 ${index + 1}`}
                      onClick={() => window.open(photo.startsWith('/uploads') ? photo : `/uploads${photo}`, '_blank')}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="receipt-footer">
          <p className="issue-date">발급일: {formatDate(receipt.issued_date)}</p>
          <p className="footer-text">
            본 영수증은 SSLMO 음식물 폐기물 수거 서비스 이용 내역을 증명하는 문서입니다.
          </p>
        </div>

        <div className="receipt-actions no-print">
          <button onClick={handlePrint} className="print-btn">
            🖨️ 인쇄하기
          </button>
          <Link to="/history" className="back-btn">
            수거 내역으로 돌아가기
          </Link>
        </div>
      </div>
    </div>
  );
}